// packages/web/src/components/FieldValueList.tsx — ported 2026-07-29 from
// the Rexx fork's 2026-07-21 UI rework: the read-only half of every detail
// screen. Renders ONLY fields that have a value (display the information we
// have, don't display what we don't so there aren't always big gaps) —
// label above value, plain text, no input chrome. The edit half is the
// existing RecordForm, mounted inline (not a modal) by the screen when Edit
// is pressed.
import { formatFieldValue, isFieldEmpty } from '../lib/fieldDisplay';
import type { RecordRow, RecordTypeSchema } from '../lib/api';
import './FieldValueList.css';

export interface FieldValueListProps {
  schema: RecordTypeSchema;
  record: RecordRow;
  /** Field names to render, in order. Omit to render every schema field
   * that has a value (declaration order). Screens that want a specific
   * lead order (e.g. a work order's client/site pair pinned above
   * everything) pass this explicitly. */
  only?: string[];
  /** Field names to always skip even if populated — e.g. a screen that
   * renders its rollup fields (visit_count, invoice_total_sum, ...) as its
   * own styled money block, not as a plain field row. */
  exclude?: string[];
}

export function FieldValueList({ schema, record, only, exclude }: FieldValueListProps) {
  const excludeSet = new Set(exclude ?? []);
  const orderedFields = only
    ? only.map((name) => schema.fields.find((f) => f.name === name)).filter((f): f is NonNullable<typeof f> => Boolean(f))
    : schema.fields;
  const rows = orderedFields.filter((f) => !excludeSet.has(f.name) && !isFieldEmpty(record.fields[f.name]));

  if (rows.length === 0) return null;

  return (
    <dl class="fvl">
      {rows.map((f) => (
        <div class="fvl__row" key={f.name}>
          <dt class="fvl__label">{f.label}</dt>
          <dd class="fvl__value">{formatFieldValue(f, record.fields[f.name])}</dd>
        </div>
      ))}
    </dl>
  );
}
